import { logger } from "./logger";

// Полифил для requestIdleCallback
declare global {
  interface Window {
    jsArray?: {
      values: Array<{
        event: string;
        detail?: string;
        locationHref: string;
        documentReferrer: string;
        timestamp: number;
        formattedTimestamp?: string;
      }>;
      splice: (start: number, deleteCount?: number) => any[];
    };
  }
}

// Полифил для браузеров без requestIdleCallback
if (typeof window !== "undefined" && !window.requestIdleCallback) {
  window.requestIdleCallback = function (
    callback: IdleRequestCallback,
    _options: IdleRequestOptions = {}
  ) {
    const start = Date.now();

    return setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining() {
          return Math.max(0, 50 - (Date.now() - start));
        },
      });
    }, 1) as any;
  };

  window.cancelIdleCallback = function (id: number) {
    clearTimeout(id);
  };
}

type EventData = {
  event: string;
  detail?: string;
  locationHref: string;
  documentReferrer: string;
  timestamp: number;
  formattedTimestamp?: string;
};

class EventScheduler {
  private batchSize: number;
  private captureServer: string;
  private isProcessing: boolean = false;

  constructor(batchSize: number = 10) {
    this.batchSize = batchSize;
    this.captureServer = this.getCaptureServerFromEnv();

    if (!this.captureServer) {
      logger.error("CAPTURE_SERVER not found in environment");
      return;
    }

    logger.debug(
      `EventScheduler initialized with batch size: ${this.batchSize}, server: ${this.captureServer}`
    );

    this.scheduleProcessing();
  }

  private getCaptureServerFromEnv(): string {
    // First try to get from build-time injected environment variables
    if (typeof process !== "undefined" && process.env?.CAPTURE_SERVER) {
      return process.env.CAPTURE_SERVER;
    }

    // Try import.meta.env for Vite builds (with proper type checking)
    try {
      if (import.meta && (import.meta as any).env?.CAPTURE_SERVER) {
        return (import.meta as any).env.CAPTURE_SERVER;
      }
    } catch (e) {
      // import.meta might not be available in all environments
    }

    // For browser environment, try global variables
    if (typeof window !== "undefined" && (window as any).CAPTURE_SERVER) {
      return (window as any).CAPTURE_SERVER;
    }

    // Fallback for development
    return "http://localhost:3000/capture";
  }

  private scheduleProcessing(): void {
    if (!window.requestIdleCallback) {
      logger.warn("requestIdleCallback not available");
      return;
    }

    window.requestIdleCallback(
      (deadline) => {
        this.processEvents(deadline);
      },
      { timeout: 5000 }
    );
  }

  private async processEvents(_deadline: IdleDeadline): Promise<void> {
    if (this.isProcessing) {
      logger.debug("Already processing events, skipping");
      this.scheduleNextProcessing();
      return;
    }

    if (!window.jsArray || !window.jsArray.values) {
      logger.debug("jsArray not available");
      this.scheduleNextProcessing();
      return;
    }

    const availableEvents = window.jsArray.values.length;
    if (availableEvents === 0) {
      logger.debug("No events to process");
      this.scheduleNextProcessing();
      return;
    }

    this.isProcessing = true;

    try {
      // Получаем N первых элементов
      const eventsToSend = window.jsArray.values.slice(0, this.batchSize);

      if (eventsToSend.length === 0) {
        logger.debug("No events to send");
        return;
      }

      logger.debug(`Processing ${eventsToSend.length} events`, eventsToSend);

      // Отправляем события на сервер
      const success = await this.sendEvents(eventsToSend);

      if (success) {
        // Удаляем отправленные события из массива
        window.jsArray.splice(0, eventsToSend.length);
        logger.info(
          `Successfully sent and removed ${eventsToSend.length} events`
        );
      } else {
        logger.warn("Failed to send events, keeping them in queue");
      }
    } catch (error) {
      logger.error("Error processing events:", error);
    } finally {
      this.isProcessing = false;
      this.scheduleNextProcessing();
    }
  }

  private async sendEvents(events: EventData[]): Promise<boolean> {
    try {
      const response = await fetch(this.captureServer, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(events),
      });

      if (!response.ok) {
        logger.warn(`Server responded with status: ${response.status}`);
        return false;
      }

      logger.info("Events sent successfully");
      return true;
    } catch (error) {
      logger.error("Network error sending events:", error);
      return false;
    }
  }

  private scheduleNextProcessing(): void {
    // Планируем следующую обработку через некоторое время
    setTimeout(() => {
      this.scheduleProcessing();
    }, 1000); // Проверяем каждую секунду
  }

  // Публичный метод для изменения размера батча
  setBatchSize(size: number): void {
    this.batchSize = size;
    logger.info(`Batch size updated to: ${this.batchSize}`);
  }

  // Публичный метод для получения статистики
  getStatus(): { batchSize: number; isProcessing: boolean; queueSize: number } {
    return {
      batchSize: this.batchSize,
      isProcessing: this.isProcessing,
      queueSize: window.jsArray?.values?.length || 0,
    };
  }
}

// Инициализация планировщика при загрузке
if (typeof window !== "undefined") {
  logger.debug("Initializing rscript EventScheduler");

  // Ждем, пока jsArray будет доступен
  const waitForJsArray = () => {
    if (window.jsArray) {
      const scheduler = new EventScheduler(10); // Батч размером 10 событий
      (window as any).eventScheduler = scheduler;
      logger.debug("EventScheduler initialized and attached to window");
    } else {
      // Повторяем через 100мс если jsArray еще не готов
      setTimeout(waitForJsArray, 100);
    }
  };

  waitForJsArray();
}

export { EventScheduler };
